import { Document, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { DateTime } from "luxon";
import React from "react";
import type { Department } from "../../contexts/AuthProvider";
import { getShiftTypeFromStartTime } from "../../utils/dateUtils";
import { formatPersonFullName } from "../../utils/formatters";
import { type Day, getRolePriority, type HospitalData, type Nurse, type NurseInShift, ShiftType } from "../../utils/models";
import { formatDateRange, formatNumber, getCensus, getNursesHoursData, getSnfWantedNursesHours, groupNursesByRole } from "../../utils/utilFunctions";
import chargeImage from "./assets/charge.png";
import logoImage from "./assets/logo.png";
import { getNursesForShiftRow } from "./staffingPdfUtils";

const pdfStyles = StyleSheet.create({
	page: {
		paddingTop: 20,
		paddingBottom: 0,
		paddingLeft: 30,
		paddingRight: 30,
		fontSize: 12,
		fontFamily: "Helvetica",
	},
	header: {
		fontSize: 24,
		marginBottom: 20,
		textAlign: "center",
	},
	subHeader: {
		fontSize: 12,
		marginBottom: 10,
		flexDirection: "row",
		justifyContent: "space-between",
	},
	table: {
		display: "flex",
		flexDirection: "column",
	},
	tableRow: {
		flexDirection: "row",
	},
	tableCol: {
		width: "13.5%",
		padding: 5,
		border: "1pt solid #000",
	},
	tableHeader: {
		fontWeight: "bold",
	},
	shiftLabel: {
		width: "5%",
		height: 195,
		padding: 5,
		fontWeight: "bold",
		borderLeft: "1pt solid #000",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	shiftLabelText: {
		transform: "rotate(-90deg)",
		transformOrigin: "center",
		width: 195,
		textAlign: "center",
		fontSize: 12,
	},
	tableCell: {
		fontSize: 8,
		padding: 2,
		color: "#000000",
	},
	nursesAmount: {
		fontSize: 9,
		color: "#000000",
		marginBottom: 2,
	},
	roleLabel: {
		fontSize: 12,
		fontWeight: "extrabold",
		marginTop: 3,
		marginBottom: 3,
	},
	logoContainer: {
		position: "absolute",
		bottom: 5,
		right: 10,
		width: 100,
		height: 50,
	},
	logo: {
		width: "100%",
		height: "100%",
		objectFit: "contain",
	},
	// Simple, static border styles
	headerCellContainer: {
		width: "5%",
		padding: 5,
	},
	headerCell: {
		width: "13.5%",
		padding: 5,
		borderBottom: "1pt solid #000",
		alignItems: "center",
		justifyContent: "center",
	},
	dataCell: {
		width: "13.5%",
		padding: 5,
		borderRight: "1pt solid #000",
		borderBottom: "1pt solid #000",
	},
	pendingDelete: {
		fontSize: 8,
		padding: 2,
		textDecoration: "line-through",
		backgroundColor: "#ffebee",
		color: "#d32f2f",
	},
	pending: {
		fontSize: 8,
		padding: 2,
		backgroundColor: "#e8f5e9",
		color: "#2e7d32",
	},
	chargeContainer: {
		flexDirection: "row",
		alignItems: "center",
	},
	chargeIcon: {
		width: 12,
		height: 12,
		marginLeft: 4,
	},
	pto: {
		fontSize: 8,
		padding: 2,
		color: "#000000",
	},
	ptoName: {
		textDecoration: "line-through",
	},
});

interface StaffingPDFProps {
	weekIndices: number[];
	hospitalData: HospitalData;
	chosenDepartment: Department;
	isSnf: boolean;
	ptoShiftsByDate?: Record<string, NurseInShift[]>;
	/** The department's name-order flag — the PDF renders outside the app's providers, so it can't read it itself. */
	lastNameFirst: boolean;
}

const getDayName = (date: DateTime): string => {
	return date.toFormat("ccc").toUpperCase();
};

/**
 * Returns PTO shifts that belong to the given PDF row.
 * Row order is fixed: index 0 = Morning (day), index 1 = Night.
 */
function getPtoShiftsForRow(allPtoForDate: NurseInShift[], shiftIndex: number): NurseInShift[] {
	const rowIsDayShift = shiftIndex === 0;

	return allPtoForDate.filter((s) => {
		const st = (s.shiftType ?? "").toLowerCase();
		const ptoIsDayShift =
			st === "morning" || st === "day" || (st === "" && (s.startTime?.isValid ? getShiftTypeFromStartTime(s.startTime) === ShiftType.Day : false));
		return rowIsDayShift === ptoIsDayShift;
	});
}

type RoleToNursesInShift = [role: string, nursesInShift: NurseInShift[]];
const sortNursesByRole = ([role1]: RoleToNursesInShift, [role2]: RoleToNursesInShift): number => {
	return getRolePriority([role2]) - getRolePriority([role1]);
};

export const StaffingPDF: React.FC<StaffingPDFProps> = ({ weekIndices, hospitalData, chosenDepartment, isSnf, ptoShiftsByDate = {}, lastNameFirst }) => {
	return (
		<Document>
			{weekIndices.map((weekIndex) => (
				<Page key={weekIndex} size="A4" orientation="landscape" style={pdfStyles.page}>
					<Text style={pdfStyles.header}>Staffing Report</Text>
					<View style={pdfStyles.subHeader}>
						<Text>
							{formatDateRange(hospitalData?.weeks[weekIndex]?.weeklyShifts[0]?.days[0]?.date, hospitalData?.weeks[weekIndex]?.weeklyShifts[0]?.days[6]?.date)}
						</Text>
						<Text>{chosenDepartment.display_name}</Text>
						<Text style={[{ textDecorationStyle: "italic", fontSize: 10, color: "gray" }]}>As of {DateTime.now().toFormat("yyyy-MM-dd HH:mm")}</Text>
					</View>
					<View style={pdfStyles.table}>
						<View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
							<View style={pdfStyles.headerCellContainer} />
							{hospitalData.weeks[weekIndex].weeklyShifts[0].days.map((day: Day, index: number) => (
								<View style={pdfStyles.headerCell} key={index}>
									<Text>{getDayName(day.date)}</Text>
									<Text>{day.date.day}</Text>
									<Text style={{ fontSize: 10 }}>Census: {getCensus(hospitalData.weeks[weekIndex], index)}</Text>
									{isSnf && (
										<Text style={{ fontSize: 10 }}>
											{(() => {
												const nursesHoursData = getNursesHoursData(hospitalData.weeks[weekIndex], index);
												return `PPD: ${formatNumber(nursesHoursData.Total.sumOfHours / getCensus(hospitalData.weeks[weekIndex], index))} / ${formatNumber(getSnfWantedNursesHours(hospitalData.weeks[weekIndex], index, chosenDepartment) / getCensus(hospitalData.weeks[weekIndex], index))}`;
											})()}
										</Text>
									)}
								</View>
							))}
						</View>
						{["Morning", "Night"].map((shiftName, shiftIndex) => {
							const rowShiftType = shiftIndex === 0 ? ShiftType.Day : ShiftType.Night;
							const week = hospitalData.weeks[weekIndex];
							// Calendar scaffold only: every shift slot holds the same 7 dates, so any slot
							// gives us the day axis. Staff come from ALL slots below, not from this one.
							const weekDays = week.weeklyShifts[0].days;
							return (
								<View style={pdfStyles.tableRow} key={shiftName}>
									<View style={pdfStyles.shiftLabel}>
										<Text style={pdfStyles.shiftLabelText}>{shiftName}</Text>
									</View>
									{weekDays.map((day: Day, dayIndex: number) => {
										const dateKey = day.date.toFormat("yyyy-MM-dd");
										const allPtoForDate = ptoShiftsByDate[dateKey] ?? [];
										const ptoForDay = getPtoShiftsForRow(allPtoForDate, shiftIndex);
										// Take nurses from EVERY shift slot for this day, then place each into the
										// Morning/Night row by their actual start time — never by which slot they
										// were stored in. See getNursesForShiftRow.
										const regularNursesForRow = getNursesForShiftRow(week, dayIndex, rowShiftType);
										const allNurses = [...regularNursesForRow, ...ptoForDay];
										const groupedNurses = groupNursesByRole(allNurses, hospitalData.nurses);
										return (
											<View style={pdfStyles.dataCell} key={dayIndex}>
												<View style={{ flexDirection: "row" }}>
													<Text style={pdfStyles.nursesAmount}>Staff members: </Text>
													<Text style={[pdfStyles.nursesAmount, { fontSize: 10 }]}>{regularNursesForRow.filter((n) => !n.isPendingToDelete).length}</Text>
												</View>
												{Object.entries(groupedNurses)
													.sort(sortNursesByRole)
													.map(([role, nurses]) => (
														<React.Fragment key={role}>
															<Text style={pdfStyles.roleLabel}>{role.match(/\((.*?)\)/)?.[1] || role}</Text>
															{nurses
																.sort((a, b) => (b.charge ? 1 : 0) - (a.charge ? 1 : 0))
																.map((nurse: NurseInShift, nurseIndex: number) => {
																	const nurseData = hospitalData.nurses.find((n: Nurse) => n.id === nurse.nurseId);
																	const isPto = nurse.message === "PTO Removal";

																	// Determine text style based on nurse status
																	let textStyle = pdfStyles.tableCell;
																	if (isPto) {
																		textStyle = pdfStyles.pto;
																	} else if (nurse.isPendingToDelete) {
																		textStyle = pdfStyles.pendingDelete;
																	} else if (nurse.isPending) {
																		textStyle = pdfStyles.pending;
																	}

																	const marginBottom = nurseIndex === nurses.length - 1 ? 3 : 0;
																	const displayName = nurseData ? formatPersonFullName(nurseData, lastNameFirst) : "";

																	return (
																		<View key={nurseIndex} style={[pdfStyles.chargeContainer, { marginBottom }]}>
																			<Text style={textStyle}>
																				{nurse.isPending && !nurse.isPendingToDelete && "+ "}
																				{(nurseIndex + 1).toString()}.{" "}
																				{isPto ? (
																					<>
																						<Text style={pdfStyles.ptoName}>{displayName}</Text>
																						{" - Time Off"}
																					</>
																				) : (
																					displayName
																				)}
																			</Text>
																			{nurse.charge && <Image src={chargeImage} style={pdfStyles.chargeIcon} />}
																		</View>
																	);
																})}
														</React.Fragment>
													))}
											</View>
										);
									})}
								</View>
							);
						})}
					</View>
					<View style={pdfStyles.logoContainer}>
						<Image style={pdfStyles.logo} src={logoImage} />
					</View>
				</Page>
			))}
		</Document>
	);
};
